import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/api/client";

export type ProjectEstado = "activo" | "pausado" | "completado" | "archivado";
export type Prioridad = "baja" | "media" | "alta";
export type TareaEstado = "pendiente" | "progreso" | "revision" | "completado";
export type StoryEstado = "pendiente" | "progreso" | "hecha";

export interface UserRef {
  id: number;
  nombre: string;
}

export interface Project {
  id: number;
  nombre: string;
  descripcion: string | null;
  estado: ProjectEstado;
  color: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  responsable_id: number | null;
  responsable?: UserRef | null;
  creador?: UserRef | null;
  tareas_count?: number;
  archivos_count?: number;
  user_stories_count?: number;
  user_stories?: UserStory[];
  archivos?: ProjectFile[];
}

export interface ProjectFile {
  id: number;
  proyecto_id: number;
  nombre_original: string;
  mime: string | null;
  tamano: number;
  subido_por: number | null;
  autor?: UserRef | null;
  creado_en: string;
}

export interface Criterio {
  id: number;
  texto: string;
  cumplido: boolean;
  orden: number;
}

export interface UserStory {
  id: number;
  proyecto_id: number;
  titulo: string;
  descripcion: string | null;
  prioridad: Prioridad;
  estado: StoryEstado;
  orden: number;
  criterios?: Criterio[];
}

export interface Subtarea {
  id: number;
  titulo: string;
  completada: boolean;
  orden: number;
}

export interface ProjectTask {
  id: number;
  user_story_id: number | null;
  titulo: string;
  descripcion: string | null;
  estado: TareaEstado;
  prioridad: Prioridad;
  asignado_id: number | null;
  asignado: UserRef | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  orden: number;
  subtareas: Subtarea[];
}

const BASE = "/projects";

export interface PizarraCard {
  id: number;
  columna_id: number;
  texto: string;
  color: string;
  orden: number;
  votos?: number;
  votado?: boolean;
}

export interface PizarraColumn {
  id: number;
  proyecto_id: number;
  titulo: string;
  orden: number;
  tarjetas: PizarraCard[];
}

export interface CanvasNote {
  id: number;
  proyecto_id: number;
  texto: string | null;
  color: string;
  x: number;
  y: number;
  ancho: number;
  alto: number;
}

export interface CanvasConnection {
  id: number;
  desde_nota_id: number;
  hasta_nota_id: number;
}

export const projectKeys = {
  all: ["projects"] as const,
  list: () => [...projectKeys.all, "list"] as const,
  detail: (id: number) => [...projectKeys.all, "detail", id] as const,
  tasks: (id: number) => [...projectKeys.all, "tasks", id] as const,
  board: (id: number) => [...projectKeys.all, "board", id] as const,
  canvas: (id: number) => [...projectKeys.all, "canvas", id] as const,
};

export function useMembers() {
  return useQuery({
    queryKey: [...projectKeys.all, "members"],
    queryFn: async () => (await api.get<{ data: UserRef[] }>(`${BASE}/members`)).data.data,
    staleTime: 5 * 60 * 1000,
  });
}

/* ── Proyectos ─────────────────────────────────────────── */

export function useProjects() {
  return useQuery({
    queryKey: projectKeys.list(),
    queryFn: async () => (await api.get<{ data: Project[] }>(BASE)).data.data,
  });
}

export function useProject(id: number) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: async () => (await api.get<{ data: Project }>(`${BASE}/${id}`)).data.data,
    enabled: id > 0,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Project>) =>
      (await api.post<{ data: Project }>(BASE, payload)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.list() }),
  });
}

export function useUpdateProject(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Project>) =>
      (await api.patch<{ data: Project }>(`${BASE}/${id}`, payload)).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.list() });
      qc.invalidateQueries({ queryKey: projectKeys.detail(id) });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`${BASE}/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.list() }),
  });
}

/* ── Archivos ──────────────────────────────────────────── */

export function useUploadFiles(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]) => {
      const fd = new FormData();
      files.forEach((f) => fd.append("archivos[]", f));
      return (await api.post(`${BASE}/${projectId}/files`, fd)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) }),
  });
}

export function useDeleteFile(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: number) => (await api.delete(`${BASE}/files/${fileId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) }),
  });
}

export async function downloadProjectFile(file: ProjectFile) {
  const res = await api.get(`${BASE}/files/${file.id}/download`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.nombre_original;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/* ── User stories ──────────────────────────────────────── */

export function useCreateStory(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<UserStory>) =>
      (await api.post<{ data: UserStory }>(`${BASE}/${projectId}/stories`, payload)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) }),
  });
}

export function useUpdateStory(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<UserStory> }) =>
      (await api.patch<{ data: UserStory }>(`${BASE}/stories/${id}`, payload)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) }),
  });
}

export function useDeleteStory(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`${BASE}/stories/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) }),
  });
}

export function useCriterio(projectId: number) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
  return {
    create: useMutation({
      mutationFn: async ({ storyId, texto }: { storyId: number; texto: string }) =>
        (await api.post(`${BASE}/stories/${storyId}/criterios`, { texto })).data,
      onSuccess: invalidate,
    }),
    toggle: useMutation({
      mutationFn: async (criterioId: number) =>
        (await api.patch(`${BASE}/criterios/${criterioId}/toggle`)).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (criterioId: number) =>
        (await api.delete(`${BASE}/criterios/${criterioId}`)).data,
      onSuccess: invalidate,
    }),
  };
}

/* ── Tareas y subtareas ────────────────────────────────── */

export function useProjectTasks(projectId: number) {
  return useQuery({
    queryKey: projectKeys.tasks(projectId),
    queryFn: async () =>
      (await api.get<{ data: ProjectTask[] }>(`${BASE}/${projectId}/tasks`)).data.data,
    enabled: projectId > 0,
  });
}

export function useCreateTask(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<ProjectTask>) =>
      (await api.post(`${BASE}/${projectId}/tasks`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.tasks(projectId) }),
  });
}

export function useUpdateTask(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<ProjectTask> }) =>
      (await api.patch(`${BASE}/tasks/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.tasks(projectId) }),
  });
}

export function useDeleteTask(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`${BASE}/tasks/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.tasks(projectId) }),
  });
}

export function useSubtask(projectId: number) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: projectKeys.tasks(projectId) });
  return {
    create: useMutation({
      mutationFn: async ({ taskId, titulo }: { taskId: number; titulo: string }) =>
        (await api.post(`${BASE}/tasks/${taskId}/subtasks`, { titulo })).data,
      onSuccess: invalidate,
    }),
    toggle: useMutation({
      mutationFn: async (subtaskId: number) =>
        (await api.patch(`${BASE}/subtasks/${subtaskId}/toggle`)).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (subtaskId: number) =>
        (await api.delete(`${BASE}/subtasks/${subtaskId}`)).data,
      onSuccess: invalidate,
    }),
  };
}

/* ── Pizarra brainstorming ─────────────────────────────── */

export function useBoard(projectId: number) {
  return useQuery({
    queryKey: projectKeys.board(projectId),
    queryFn: async () =>
      (await api.get<{ data: PizarraColumn[] }>(`${BASE}/${projectId}/board`)).data.data,
    enabled: projectId > 0,
  });
}

export function useBoardActions(projectId: number) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: projectKeys.board(projectId) });
  return {
    addColumn: useMutation({
      mutationFn: async (titulo: string) =>
        (await api.post(`${BASE}/${projectId}/board/columns`, { titulo })).data,
      onSuccess: invalidate,
    }),
    renameColumn: useMutation({
      mutationFn: async ({ id, titulo }: { id: number; titulo: string }) =>
        (await api.patch(`${BASE}/board/columns/${id}`, { titulo })).data,
      onSuccess: invalidate,
    }),
    deleteColumn: useMutation({
      mutationFn: async (id: number) => (await api.delete(`${BASE}/board/columns/${id}`)).data,
      onSuccess: invalidate,
    }),
    addCard: useMutation({
      mutationFn: async ({ columnId, texto, color }: { columnId: number; texto: string; color: string }) =>
        (await api.post(`${BASE}/board/columns/${columnId}/cards`, { texto, color })).data,
      onSuccess: invalidate,
    }),
    updateCard: useMutation({
      mutationFn: async ({ id, payload }: { id: number; payload: Partial<PizarraCard> }) =>
        (await api.patch(`${BASE}/board/cards/${id}`, payload)).data,
      onSuccess: invalidate,
    }),
    deleteCard: useMutation({
      mutationFn: async (id: number) => (await api.delete(`${BASE}/board/cards/${id}`)).data,
      onSuccess: invalidate,
    }),
    vote: useMutation({
      mutationFn: async (id: number) => (await api.post(`${BASE}/board/cards/${id}/vote`)).data,
      onSuccess: invalidate,
    }),
    reorder: useMutation({
      mutationFn: async (cards: { id: number; columna_id: number; orden: number }[]) =>
        (await api.post(`${BASE}/board/reorder`, { cards })).data,
      onSuccess: invalidate,
    }),
    toTask: useMutation({
      mutationFn: async (id: number) => (await api.post(`${BASE}/board/cards/${id}/to-task`)).data,
      onSuccess: () => {
        invalidate();
        qc.invalidateQueries({ queryKey: projectKeys.tasks(projectId) });
        qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      },
    }),
  };
}

/* ── Lienzo (Miro) ─────────────────────────────────────── */

export function useCanvas(projectId: number) {
  return useQuery({
    queryKey: projectKeys.canvas(projectId),
    queryFn: async () =>
      (await api.get<{ notas: CanvasNote[]; conexiones: CanvasConnection[] }>(
        `${BASE}/${projectId}/canvas`,
      )).data,
    enabled: projectId > 0,
  });
}

export function useCanvasActions(projectId: number) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: projectKeys.canvas(projectId) });
  return {
    addNote: useMutation({
      mutationFn: async (payload: { x: number; y: number; texto?: string; color?: string }) =>
        (await api.post<{ data: CanvasNote }>(`${BASE}/${projectId}/canvas/notes`, payload)).data.data,
      onSuccess: invalidate,
    }),
    updateNote: useMutation({
      mutationFn: async ({ id, payload }: { id: number; payload: Partial<CanvasNote> }) =>
        (await api.patch(`${BASE}/canvas/notes/${id}`, payload)).data,
      onSuccess: invalidate,
    }),
    deleteNote: useMutation({
      mutationFn: async (id: number) => (await api.delete(`${BASE}/canvas/notes/${id}`)).data,
      onSuccess: invalidate,
    }),
    addConnection: useMutation({
      mutationFn: async (payload: { desde_nota_id: number; hasta_nota_id: number }) =>
        (await api.post(`${BASE}/${projectId}/canvas/connections`, payload)).data,
      onSuccess: invalidate,
    }),
    deleteConnection: useMutation({
      mutationFn: async (id: number) => (await api.delete(`${BASE}/canvas/connections/${id}`)).data,
      onSuccess: invalidate,
    }),
  };
}
